import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, arrayMove, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { QRCodeSVG } from 'qrcode.react';
import { tagColor } from '../utils/tagColor.js';

// ─── constants ────────────────────────────────────────────────────────────────
const COLORS = ['blue', 'teal', 'green', 'yellow'];
const COLOR_DOT = {
  blue: 'bg-blue-500', teal: 'bg-teal-500', green: 'bg-green-500', yellow: 'bg-yellow-400',
};
const COLOR_BADGE = {
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  teal: 'bg-teal-100 text-teal-700 border-teal-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
};
const SECTION_ICONS = {
  agenda: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
    </svg>
  ),
  materials: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  ),
  gems: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  ),
  notebooks: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
    </svg>
  ),
  prompts: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  settings: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  survey: (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
};

const SECTIONS_NAV = [
  { id: 'agenda',             label: 'アジェンダ' },
  { id: 'gemini-spreadsheet', label: 'Gemini in スプレッドシート' },
  { id: 'gemini-docs',        label: 'Gemini in ドキュメント' },
  { id: 'gemini-gmail',       label: 'Gemini in Gmail' },
  { id: 'gems',               label: 'Gems' },
  { id: 'notebooks',          label: 'NotebookLM' },
  { id: 'prompts',            label: 'プロンプト' },
  { id: 'settings',           label: '設定' },
  { id: 'tenants',            label: 'テナント管理' },  // 本社のみ表示
];

// ─── api helpers ──────────────────────────────────────────────────────────────
function apiCall(method, path, body, token) {
  return fetch(`/api${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  }).then(async r => {
    const data = await r.json();
    if (r.status === 401) {
      localStorage.removeItem('admin_token');
      window.location.reload();
      throw new Error('セッションが切れました。再ログインしてください。');
    }
    if (!r.ok) throw new Error(data.error || 'エラーが発生しました');
    return data;
  });
}

// ─── Toast ────────────────────────────────────────────────────────────────────
function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-xl shadow-lg text-sm font-medium text-white transition-all border ${toast.type === 'error' ? 'bg-red-600 border-red-500' : 'bg-emerald-600 border-emerald-500'}`}>
      {toast.message}
    </div>
  );
}

// ─── File Upload Button ───────────────────────────────────────────────────────
function FileUploadButton({ token, onUploaded, onError, accept, label }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'アップロードに失敗しました');
      onUploaded(data.url);
    } catch (err) {
      onError?.(err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <>
      <input ref={inputRef} type="file" accept={accept} className="hidden" onChange={handleChange} />
      <button
        type="button"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-dashed border-gray-300 text-sm text-slate-500 hover:bg-slate-50 hover:border-gray-400 whitespace-nowrap flex-shrink-0 disabled:opacity-50 transition-colors self-start"
      >
        {uploading ? (
          <>
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            アップロード中...
          </>
        ) : (
          <>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
            </svg>
            {label}
          </>
        )}
      </button>
    </>
  );
}

// ─── Tag Field ────────────────────────────────────────────────────────────────
const NEW_TAG_SENTINEL = '__new__';

function TagField({ value, onChange, existingTags }) {
  const valueInList = existingTags.includes(value);
  const [customMode, setCustomMode] = useState(() => !valueInList);
  const selectValue = customMode ? NEW_TAG_SENTINEL : (value || '');

  return (
    <div className="flex flex-col gap-2">
      <select
        className={inputCls}
        value={selectValue}
        required={!customMode}
        onChange={e => {
          if (e.target.value === NEW_TAG_SENTINEL) {
            setCustomMode(true);
            onChange('');
          } else {
            setCustomMode(false);
            onChange(e.target.value);
          }
        }}
      >
        <option value="" disabled>タグを選択...</option>
        {existingTags.map(t => <option key={t} value={t}>{t}</option>)}
        <option value={NEW_TAG_SENTINEL}>＋ 新しいタグを作成...</option>
      </select>
      {customMode && (
        <input
          required
          autoFocus
          className={inputCls}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder="例: 👥 人事・採用"
        />
      )}
    </div>
  );
}

// ─── Color Picker ─────────────────────────────────────────────────────────────
function ColorPicker({ value, onChange, disabled }) {
  return (
    <div className={`flex gap-2 ${disabled ? 'opacity-60 pointer-events-none' : ''}`}>
      {COLORS.map(c => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className={`w-7 h-7 rounded-full ${COLOR_DOT[c]} transition-all ${value === c ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'opacity-70 hover:opacity-100'}`}
        />
      ))}
    </div>
  );
}

// ─── Sources Editor ───────────────────────────────────────────────────────────
const SOURCE_TYPES = [
  { value: 'link', label: '🔗 リンク' },
  { value: 'pdf',  label: '📄 PDF'   },
  { value: 'text', label: '📝 テキスト' },
];

function SourcesEditor({ sources, onChange, token, showToast }) {
  const add = () => onChange([...sources, { id: crypto.randomUUID(), type: 'link', label: '', url: '' }]);

  const update = (id, patch) => {
    const next = sources.map(s => {
      if (s.id !== id) return s;
      const merged = { ...s, ...patch };
      if (patch.type === 'text') { delete merged.url; merged.content = merged.content || ''; }
      else { delete merged.content; merged.url = merged.url || ''; }
      return merged;
    });
    onChange(next);
  };

  const remove = (id) => onChange(sources.filter(s => s.id !== id));

  return (
    <div className="flex flex-col gap-2">
      {sources.map(src => (
        <div key={src.id} className="flex flex-col gap-2 p-3 bg-slate-50 rounded-xl border border-slate-100">
          <div className="flex items-center gap-2">
            <select
              value={src.type}
              onChange={e => update(src.id, { type: e.target.value })}
              className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700 flex-shrink-0"
            >
              {SOURCE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <input
              className={`${inputCls} flex-1 text-sm`}
              value={src.label}
              onChange={e => update(src.id, { label: e.target.value })}
              placeholder="ラベル（例: 社内規程 2024年版）"
            />
            <button
              type="button"
              onClick={() => remove(src.id)}
              className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg flex-shrink-0 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          {src.type !== 'text' ? (
            <div className="flex gap-2 items-start">
              <input
                className={`${inputCls} flex-1 text-sm`}
                value={src.url || ''}
                onChange={e => update(src.id, { url: e.target.value })}
                placeholder="https://"
              />
              {src.type === 'pdf' && (
                <FileUploadButton
                  token={token}
                  accept=".pdf,application/pdf"
                  label="PDFをアップロード"
                  onUploaded={url => update(src.id, { url })}
                  onError={msg => showToast?.(msg, 'error')}
                />
              )}
            </div>
          ) : (
            <textarea
              className={`${inputCls} h-20 resize-y text-sm`}
              value={src.content || ''}
              onChange={e => update(src.id, { content: e.target.value })}
              placeholder="テキスト内容（業務メモ、前提知識など）"
            />
          )}
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-dashed border-gray-300 text-sm text-slate-500 hover:bg-slate-50 hover:border-gray-400 transition-colors"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
        ソースを追加
      </button>
    </div>
  );
}

// ─── Sortable Item ─────────────────────────────────────────────────────────────
function SortableItem({ id, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex gap-2 items-start ${isDragging ? 'opacity-50 z-10' : ''}`}
    >
      <button
        {...attributes}
        {...listeners}
        className="mt-3 p-1.5 text-gray-300 hover:text-slate-500 cursor-grab active:cursor-grabbing flex-shrink-0"
        title="ドラッグで並び替え"
      >
        ⠿
      </button>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

// ─── Item Row ──────────────────────────────────────────────────────────────────
function ItemRow({ item, resource, onEdit, onDelete, onToggle, sections }) {
  const badge = COLOR_BADGE[item.color] || COLOR_BADGE.blue;

  // materials: セクション名を表示
  const sectionName = resource === 'materials'
    ? (sections?.find(s => s.id === item.section_id)?.name || '未分類')
    : (item.tag || item.desc || '');

  const sectionIcon = resource === 'materials'
    ? (sections?.find(s => s.id === item.section_id)?.icon || '📁')
    : null;

  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex items-center gap-3 shadow-card">
      {item.icon && <span className="text-xl flex-shrink-0">{item.icon}</span>}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${COLOR_DOT[item.color] || 'bg-gray-400'}`} />
          <span className="font-medium text-slate-800 text-sm truncate">{item.name}</span>
          {sectionName && (
            <span className={`text-xs px-2 py-0.5 rounded-full border ${badge}`}>
              {sectionIcon && <span className="mr-1">{sectionIcon}</span>}
              {sectionName}
            </span>
          )}
        </div>
        {(item.url || item.gem_url) && (
          <p className="text-xs text-slate-400 truncate mt-0.5">{item.url || item.gem_url}</p>
        )}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={() => onToggle(item)}
          className={`p-1.5 rounded-lg text-sm transition-colors ${item.visible ? 'text-green-600 hover:bg-green-50' : 'text-gray-300 hover:bg-slate-100'}`}
          title={item.visible ? '表示中（クリックで非表示）' : '非表示（クリックで表示）'}
        >
          {item.visible ? (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
            </svg>
          )}
        </button>
        <button onClick={() => onEdit(item)} className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
        <button onClick={() => onDelete(item)} className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 transition-colors">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ─── Item Form Modal ───────────────────────────────────────────────────────────
function ItemModal({ resource, item, onSave, onClose, existingTags = [], token, showToast, sections = [], customLabel }) {
  const isEdit = !!item?.id;
  const defaults = {
    gems:      { name: '', tag: '', desc: '', color: 'blue', gem_url: '', system_prompt: '', example_prompt: '', gem_type: 'shared', visible: 1 },
    notebooks: { name: '', tag: '', desc: '', icon: '📒', color: 'blue', url: '', notebook_type: 'shared', sources: [], visible: 1 },
    prompts:   { name: '', tag: '', color: 'blue', text: '', visible: 1 },
    materials: { name: '', desc: '', icon: '📄', color: 'blue', url: '', section_id: null, content: '', visible: 1 },
  };
  const [form, setForm] = useState(() => {
    const base = item || defaults[resource];
    if (resource === 'notebooks') {
      const src = base.sources;
      const parsed = Array.isArray(src) ? src : (() => { try { return JSON.parse(src || '[]'); } catch { return []; } })();
      // 既存ソースに id がない場合は付与
      return { ...base, sources: parsed.map(s => ({ id: s.id || crypto.randomUUID(), ...s })) };
    }
    return base;
  });

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const hasTagField = resource === 'gems' || resource === 'notebooks' || resource === 'prompts';
  const autoColor = hasTagField ? tagColor(form.tag) : null;
  const displayColor = autoColor || form.color || 'blue';

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  const resourceLabel = { gems: 'Gem', notebooks: 'NotebookLM', prompts: 'プロンプト', materials: '研修資料' };
  const displayResourceLabel = customLabel || resourceLabel[resource];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="font-bold text-slate-800">{isEdit ? '編集' : '追加'} — {displayResourceLabel}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-4">
          <Field label="名前 *">
            <input required className={inputCls} value={form.name} onChange={e => set('name', e.target.value)} />
          </Field>

          {hasTagField && (
            <Field label="タグ">
              <TagField
                value={form.tag}
                onChange={v => { set('tag', v); const c = tagColor(v); if (c) set('color', c); }}
                existingTags={existingTags}
              />
            </Field>
          )}

          {(resource === 'notebooks' || resource === 'materials' || resource === 'gems') && (
            <Field label="説明 *">
              <input required={resource !== 'gems'} className={inputCls} value={form.desc || ''} onChange={e => set('desc', e.target.value)} />
            </Field>
          )}

          {(resource === 'notebooks' || resource === 'materials') && (
            <Field label="アイコン（絵文字）">
              <input className={inputCls} value={form.icon} onChange={e => set('icon', e.target.value)} placeholder="📒" maxLength={2} />
            </Field>
          )}

          <Field label="カラー">
            <div className="flex items-center gap-3">
              <ColorPicker value={displayColor} onChange={v => set('color', v)} disabled={!!autoColor} />
              {autoColor && <span className="text-xs text-slate-400">タグに連動して自動設定</span>}
            </div>
          </Field>

          {/* materials: セクション選択 */}
          {resource === 'materials' && (
            <Field label="セクション">
              <select
                className={inputCls}
                value={form.section_id || ''}
                onChange={e => set('section_id', e.target.value || null)}
              >
                <option value="">未分類</option>
                {sections.map(s => (
                  <option key={s.id} value={s.id}>{s.icon} {s.name}</option>
                ))}
              </select>
            </Field>
          )}

          {/* gem_type toggle */}
          {resource === 'gems' && (
            <Field label="種別">
              <div className="flex gap-2">
                {[
                  { value: 'shared', label: '💎 共有Gem',      desc: '作成済みGemをURLで共有' },
                  { value: 'sample', label: '📝 サンプルデータ', desc: 'プロンプトを配布' },
                ].map(opt => (
                  <button key={opt.value} type="button" onClick={() => set('gem_type', opt.value)}
                    className={`flex-1 flex flex-col items-center gap-0.5 px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${form.gem_type === opt.value ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500 hover:border-gray-300'}`}>
                    <span>{opt.label}</span>
                    <span className="text-xs font-normal opacity-70">{opt.desc}</span>
                  </button>
                ))}
              </div>
            </Field>
          )}

          {/* notebook_type toggle */}
          {resource === 'notebooks' && (
            <Field label="種別">
              <div className="flex gap-2">
                {[
                  { value: 'shared', label: '📒 共有ノート',    desc: 'URLリンクで共有' },
                  { value: 'sample', label: '🗂 サンプルデータ', desc: 'ソース素材セット' },
                ].map(opt => (
                  <button key={opt.value} type="button" onClick={() => set('notebook_type', opt.value)}
                    className={`flex-1 flex flex-col items-center gap-0.5 px-3 py-2.5 rounded-xl border-2 text-sm font-medium transition-all ${form.notebook_type === opt.value ? 'border-indigo-500 bg-indigo-50 text-indigo-700' : 'border-slate-200 text-slate-500 hover:border-gray-300'}`}>
                    <span>{opt.label}</span>
                    <span className="text-xs font-normal opacity-70">{opt.desc}</span>
                  </button>
                ))}
              </div>
            </Field>
          )}

          {/* URL + QR + upload */}
          {((resource === 'materials') || (resource === 'gems' && form.gem_type !== 'sample') || (resource === 'notebooks' && form.notebook_type !== 'sample')) && (
            <Field label={resource === 'gems' ? 'Gem URL' : resource === 'materials' ? 'URL または PDF' : 'URL'}>
              <div className="flex gap-2 items-start flex-wrap">
                <input
                  className={`${inputCls} flex-1 min-w-0`}
                  type="text"
                  value={form.url ?? form.gem_url ?? ''}
                  onChange={e => resource === 'gems' ? set('gem_url', e.target.value) : set('url', e.target.value)}
                  placeholder="https://"
                />
                {resource === 'materials' && (
                  <FileUploadButton
                    token={token}
                    accept=".pdf,application/pdf"
                    label="PDFをアップロード"
                    onUploaded={url => set('url', url)}
                    onError={msg => showToast?.(msg, 'error')}
                  />
                )}
                {(form.url || form.gem_url) && (
                  <div className="flex-shrink-0 p-1 bg-slate-50 border border-slate-200 rounded-lg">
                    <QRCodeSVG value={form.url || form.gem_url} size={64} />
                  </div>
                )}
              </div>
              {form.url?.startsWith('/api/files/') && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  PDFアップロード済み
                </p>
              )}
            </Field>
          )}

          {/* materials: content（URLなしの場合のテキスト） */}
          {resource === 'materials' && !form.url && (
            <Field label="テキストコンテンツ（URLがない場合）">
              <textarea
                className={`${inputCls} h-40 resize-y`}
                value={form.content || ''}
                onChange={e => set('content', e.target.value)}
                placeholder="テンプレート内容をここに入力してください"
              />
            </Field>
          )}

          {/* sources editor for sample notebooks */}
          {resource === 'notebooks' && form.notebook_type === 'sample' && (
            <Field label="ソース">
              <SourcesEditor sources={form.sources || []} onChange={v => set('sources', v)} token={token} showToast={showToast} />
            </Field>
          )}

          {/* gem specific */}
          {resource === 'gems' && (
            <>
              <Field label="システムプロンプト">
                <textarea className={`${inputCls} h-28 resize-y`} value={form.system_prompt || ''} onChange={e => set('system_prompt', e.target.value)} />
              </Field>
              <Field label="プロンプト例">
                <textarea className={`${inputCls} h-24 resize-y`} value={form.example_prompt || ''} onChange={e => set('example_prompt', e.target.value)} />
              </Field>
            </>
          )}

          {/* prompt text */}
          {resource === 'prompts' && (
            <Field label="プロンプト本文 *">
              <textarea required className={`${inputCls} h-32 resize-y`} value={form.text || ''} onChange={e => set('text', e.target.value)} />
            </Field>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 text-sm font-medium transition-colors">
              キャンセル
            </button>
            <button type="submit"
              className="flex-1 py-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 text-sm font-medium transition-colors cursor-pointer">
              {isEdit ? '保存' : '追加'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 transition-shadow bg-white';

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</label>
      {children}
    </div>
  );
}

// ─── Resource Section ──────────────────────────────────────────────────────────
function ResourceSection({ resource, items, allTagItems, token, setData, showToast, sections, customLabel, customIcon }) {
  const [modal, setModal] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const existingTags = (resource === 'gems' || resource === 'prompts' || resource === 'notebooks')
    ? [...new Set((allTagItems || []).map(i => i.tag).filter(Boolean))]
    : [];

  const handleDragEnd = useCallback(async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex(i => i.id === active.id);
    const newIndex = items.findIndex(i => i.id === over.id);
    const reordered = arrayMove(items, oldIndex, newIndex);
    setData(prev => ({ ...prev, [resource]: reordered }));
    try {
      await apiCall('PUT', `/${resource}/reorder`, { ids: reordered.map(i => i.id) }, token);
    } catch {
      showToast('並び替えの保存に失敗しました', 'error');
    }
  }, [items, resource, token, setData, showToast]);

  const deleteR2File = (url) => {
    if (url?.startsWith('/api/files/')) {
      apiCall('DELETE', url.replace('/api', ''), null, token).catch(() => {});
    }
  };

  const handleSave = async (formData) => {
    const isEdit = !!formData.id;
    try {
      if (isEdit) {
        const original = items.find(i => i.id === formData.id);
        const oldUrl = original?.url || '';
        const newUrl = formData.url || '';
        const updated = await apiCall('PUT', `/${resource}/${formData.id}`, formData, token);
        setData(prev => ({ ...prev, [resource]: prev[resource].map(i => i.id === updated.id ? updated : i) }));
        if (oldUrl.startsWith('/api/files/') && oldUrl !== newUrl) deleteR2File(oldUrl);
      } else {
        const created = await apiCall('POST', `/${resource}`, formData, token);
        setData(prev => ({ ...prev, [resource]: [...prev[resource], created] }));
      }
      setModal(null);
      showToast(isEdit ? '更新しました' : '追加しました');
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const handleDelete = async (item) => {
    if (!confirm(`「${item.name}」を削除しますか？`)) return;
    const snapshot = items;
    setData(prev => ({ ...prev, [resource]: prev[resource].filter(i => i.id !== item.id) }));
    try {
      await apiCall('DELETE', `/${resource}/${item.id}`, null, token);
      deleteR2File(item.url);
      showToast('削除しました');
    } catch (e) {
      setData(prev => ({ ...prev, [resource]: snapshot }));
      showToast(e.message, 'error');
    }
  };

  const handleToggle = async (item) => {
    const updated = { ...item, visible: item.visible ? 0 : 1 };
    setData(prev => ({ ...prev, [resource]: prev[resource].map(i => i.id === item.id ? updated : i) }));
    try {
      await apiCall('PUT', `/${resource}/${item.id}`, updated, token);
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const label = { gems: 'Gem', notebooks: 'NotebookLM', prompts: 'プロンプト', materials: '研修資料' };
  const displayLabel = customLabel || label[resource];
  const displayIcon = customIcon || (resource === 'materials' ? '📄' : SECTION_ICONS[resource]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
          <span className="text-slate-400 text-xl">{displayIcon}</span>
          {displayLabel}
        </h2>
        <button
          onClick={() => setModal({ item: null })}
          className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 transition-colors cursor-pointer"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          追加
        </button>
      </div>

      {items.length === 0 ? (
        <div className="py-12 text-center text-slate-400 border-2 border-dashed border-slate-200 rounded-2xl">
          <p className="text-sm">まだ登録されていません</p>
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
            <div className="flex flex-col gap-2">
              {items.map(item => (
                <SortableItem key={item.id} id={item.id}>
                  <ItemRow
                    item={item}
                    resource={resource}
                    onEdit={i => setModal({ item: i })}
                    onDelete={handleDelete}
                    onToggle={handleToggle}
                    sections={sections}
                  />
                </SortableItem>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {modal && (
        <ItemModal
          resource={resource}
          item={modal.item}
          onSave={handleSave}
          onClose={() => setModal(null)}
          existingTags={existingTags}
          token={token}
          showToast={showToast}
          sections={sections}
          customLabel={customLabel}
        />
      )}
    </div>
  );
}

// ─── Section Manager（設定画面内） ────────────────────────────────────────────
function SectionManager({ sections, token, showToast, setData }) {
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', icon: '', edition: null });
  const [newForm, setNewForm] = useState({ name: '', icon: '📁', edition: null });
  const [adding, setAdding] = useState(false);
  const [filterEdition, setFilterEdition] = useState(null); // null = 全表示, 'genspark', 'claude', 'google_workspace'
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = useCallback(async ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = sections.findIndex(s => s.id === active.id);
    const newIndex = sections.findIndex(s => s.id === over.id);
    const reordered = arrayMove(sections, oldIndex, newIndex);
    setData(prev => ({ ...prev, sections: reordered }));
    try {
      await apiCall('PUT', '/sections/reorder', { ids: reordered.map(s => s.id) }, token);
    } catch {
      showToast('並び替えの保存に失敗しました', 'error');
    }
  }, [sections, token, setData, showToast]);

  const startEdit = (s) => { setEditingId(s.id); setEditForm({ name: s.name, icon: s.icon, edition: s.edition || null }); };
  const cancelEdit = () => setEditingId(null);

  const filteredSections = useMemo(() => {
    if (!filterEdition) return sections;
    return sections.filter(s => (s.edition || null) === filterEdition);
  }, [sections, filterEdition]);

  const saveEdit = async (id) => {
    try {
      const payload = { name: editForm.name, icon: editForm.icon };
      if (editForm.edition) payload.edition = editForm.edition;
      const updated = await apiCall('PUT', `/sections/${id}`, payload, token);
      setData(prev => ({ ...prev, sections: prev.sections.map(s => s.id === id ? updated : s) }));
      setEditingId(null);
      showToast('セクションを更新しました');
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  const handleDelete = async (s) => {
    if (!confirm(`「${s.name}」を削除しますか？\nこのセクションの資料は「未分類」になります。`)) return;
    const snapshot = sections;
    setData(prev => ({ ...prev, sections: prev.sections.filter(x => x.id !== s.id) }));
    try {
      await apiCall('DELETE', `/sections/${s.id}`, null, token);
      showToast('削除しました');
    } catch (e) {
      setData(prev => ({ ...prev, sections: snapshot }));
      showToast(e.message, 'error');
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const created = await apiCall('POST', '/sections', newForm, token);
      setData(prev => ({ ...prev, sections: [...prev.sections, created] }));
      setNewForm({ name: '', icon: '📁' });
      setAdding(false);
      showToast('セクションを追加しました');
    } catch (e) {
      showToast(e.message, 'error');
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-slate-700">研修資料セクション</h3>
        <button
          onClick={() => setAdding(v => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white rounded-xl text-xs font-medium hover:bg-indigo-700 transition-colors cursor-pointer"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          追加
        </button>
      </div>

      <div className="flex items-center gap-2">
        <label className="text-xs font-semibold text-slate-500 uppercase">版でフィルタ:</label>
        <select value={filterEdition || ''} onChange={e => setFilterEdition(e.target.value || null)}
          className="px-2 py-1 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-400">
          <option value="">全て表示</option>
          <option value="null">共通（版なし）</option>
          <option value="google_workspace">Google Workspace版</option>
          <option value="genspark">Genspark版</option>
          <option value="claude">Claude Cowork版</option>
        </select>
      </div>

      <p className="text-xs text-slate-400 -mt-2">
        研修資料タブでの表示グループです。ドラッグで並び替えできます。
      </p>

      {/* 新規追加フォーム */}
      {adding && (
        <form onSubmit={handleAdd} className="flex flex-col gap-2 p-3 bg-indigo-50 border border-indigo-100 rounded-xl">
          <div className="flex items-center gap-2">
            <input
              className="w-12 px-2 py-1.5 border border-slate-200 rounded-lg text-sm text-center bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
              value={newForm.icon}
              onChange={e => setNewForm(p => ({ ...p, icon: e.target.value }))}
              placeholder="📁"
              maxLength={2}
            />
            <input
              required
              autoFocus
              className={`${inputCls} flex-1`}
              value={newForm.name}
              onChange={e => setNewForm(p => ({ ...p, name: e.target.value }))}
              placeholder="セクション名"
            />
            <select value={newForm.edition || ''} onChange={e => setNewForm(p => ({ ...p, edition: e.target.value || null }))}
              className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-400">
              <option value="">共通</option>
              <option value="google_workspace">Google Workspace版</option>
              <option value="genspark">Genspark版</option>
              <option value="claude">Claude Cowork版</option>
            </select>
          </div>
          <div className="flex gap-2 justify-end">
            <button type="submit" className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 transition-colors">追加</button>
            <button type="button" onClick={() => setAdding(false)} className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-50 transition-colors">キャンセル</button>
          </div>
        </form>
      )}

      {/* セクション一覧 */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={filteredSections.map(s => s.id)} strategy={verticalListSortingStrategy}>
          <div className="flex flex-col gap-2">
            {filteredSections.map(s => (
              <SortableItem key={s.id} id={s.id}>
                {editingId === s.id ? (
                  <div className="flex flex-col gap-2 p-2 bg-slate-50 border border-slate-200 rounded-xl">
                    <div className="flex items-center gap-2">
                      <input
                        className="w-12 px-2 py-1.5 border border-slate-200 rounded-lg text-sm text-center bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        value={editForm.icon}
                        onChange={e => setEditForm(p => ({ ...p, icon: e.target.value }))}
                        maxLength={2}
                      />
                      <input
                        autoFocus
                        className={`${inputCls} flex-1`}
                        value={editForm.name}
                        onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                      />
                      <select value={editForm.edition || ''} onChange={e => setEditForm(p => ({ ...p, edition: e.target.value || null }))}
                        className="px-2 py-1.5 text-xs border border-slate-200 rounded-lg bg-white text-slate-600 focus:outline-none focus:ring-2 focus:ring-indigo-400">
                        <option value="">共通</option>
                        <option value="google_workspace">Google Workspace版</option>
                        <option value="genspark">Genspark版</option>
                        <option value="claude">Claude Cowork版</option>
                      </select>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => saveEdit(s.id)} className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg text-xs font-medium hover:bg-indigo-700 transition-colors">保存</button>
                      <button onClick={cancelEdit} className="px-3 py-1.5 bg-white border border-slate-200 text-slate-600 rounded-lg text-xs font-medium hover:bg-slate-50 transition-colors">キャンセル</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3 px-3 py-2.5 bg-white border border-slate-200 rounded-xl">
                    <span className="text-lg">{s.icon}</span>
                    <span className="flex-1">
                      <span className="text-sm font-medium text-slate-700">{s.name}</span>
                      {s.edition && <span className="ml-2 px-2 py-0.5 text-xs rounded bg-slate-100 text-slate-600">{s.edition === 'google_workspace' ? 'GW' : s.edition === 'genspark' ? 'GS' : 'CC'}</span>}
                    </span>
                    <button onClick={() => startEdit(s)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button onClick={() => handleDelete(s)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors">
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )}
              </SortableItem>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

// ─── Agenda Section ───────────────────────────────────────────────────────────

// ─── Tenant Section ──────────────────────────────────────────────────────────
function TenantSection({ tenants = [], token, showToast, setData }) {
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ name: '', region: '', password: '', participant_password: '', slug: '' });

  const openNew = () => { setForm({ name: '', region: '', password: '', participant_password: '', slug: '' }); setEditItem(null); setShowForm(true); };
  const openEdit = (t) => { setForm({ name: t.name, region: t.region || '', password: '', participant_password: '', slug: t.slug || '' }); setEditItem(t); setShowForm(true); };

  const save = async () => {
    if (!form.name.trim()) return showToast('テナント名を入力してください', 'error');
    try {
      if (editItem) {
        await apiCall('PUT', '/tenants/' + editItem.id, form, token);
        showToast('更新しました');
      } else {
        await apiCall('POST', '/tenants', form, token);
        showToast('テナントを作成しました');
      }
      const d = await apiCall('GET', '/data', null, token);
      setData(d);
      setShowForm(false);
    } catch (e) { showToast(e.message, 'error'); }
  };

  const del = async (t) => {
    if (!confirm(t.name + ' を削除しますか？')) return;
    try {
      await apiCall('DELETE', '/tenants/' + t.id, null, token);
      showToast('削除しました');
      const d = await apiCall('GET', '/data', null, token);
      setData(d);
    } catch (e) { showToast(e.message, 'error'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-800">テナント管理</h2>
        <button onClick={openNew} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
          + テナント追加
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
        <p className="font-medium mb-1">📋 テナントとは</p>
        <p>各地域・拠点ごとの管理単位です。テナントごとに管理者パスワード・参加者パスワード・独自コンテンツを設定できます。</p>
        <p className="mt-1">本社（HQ）が作成したコンテンツは全テナントに共通表示されます。</p>
      </div>

      <div className="space-y-3">
        {tenants.length === 0 && (
          <div className="text-center py-8 text-gray-400">テナントがありません</div>
        )}
        {tenants.map(t => (
          <div key={t.id} className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold text-gray-800 text-base">{t.name}</div>
                <div className="text-xs text-gray-400 mt-0.5">地域: {t.region || '未設定'}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => openEdit(t)} className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">編集</button>
                <button onClick={() => del(t)} className="px-3 py-1.5 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50">削除</button>
              </div>
            </div>
            {/* URLセクション */}
            <div className="flex flex-col gap-2 bg-slate-50 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 w-20 shrink-0">🔑 管理者URL</span>
                <code className="flex-1 text-xs bg-white border border-slate-200 rounded px-2 py-1 text-slate-700 truncate font-mono">{window.location.origin}/admin?tenant={t.id}</code>
                <button onClick={() => { navigator.clipboard.writeText(window.location.origin + '/admin?tenant=' + t.id); }}
                  className="shrink-0 px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-600 rounded text-xs transition-colors">コピー</button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 w-20 shrink-0">👥 参加者URL</span>
                {t.slug ? (
                  <>
                    <code className="flex-1 text-xs bg-white border border-green-200 rounded px-2 py-1 text-green-700 truncate font-mono">{window.location.origin}/t/{t.slug}</code>
                    <button onClick={() => { navigator.clipboard.writeText(window.location.origin + '/t/' + t.slug); }}
                      className="shrink-0 px-2 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded text-xs transition-colors">コピー</button>
                  </>
                ) : (
                  <>
                    <code className="flex-1 text-xs bg-white border border-slate-200 rounded px-2 py-1 text-slate-700 truncate font-mono">{window.location.origin}/?tenant={t.id}</code>
                    <button onClick={() => { navigator.clipboard.writeText(window.location.origin + '/?tenant=' + t.id); }}
                      className="shrink-0 px-2 py-1 bg-slate-200 hover:bg-slate-300 text-slate-600 rounded text-xs transition-colors">コピー</button>
                    <span className="text-xs text-amber-500 shrink-0">スラッグ未設定</span>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold mb-4">{editItem ? 'テナント編集' : 'テナント追加'}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">テナント名 *</label>
                <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="例：東京支社" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">地域</label>
                <input value={form.region} onChange={e => setForm(f => ({...f, region: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="例：関東" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {editItem ? '管理者パスワード（変更する場合のみ）' : '管理者パスワード'}
                </label>
                <input type="password" value={form.password} onChange={e => setForm(f => ({...f, password: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="パスワードを入力" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {editItem ? '参加者パスワード（変更する場合のみ）' : '参加者パスワード'}
                </label>
                <input type="password" value={form.participant_password} onChange={e => setForm(f => ({...f, participant_password: e.target.value}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="参加者パスワードを入力" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">短縮スラッグ（例: toshin → /t/toshin）</label>
                <input type="text" value={form.slug} onChange={e => setForm(f => ({...f, slug: e.target.value.replace(/[^a-z0-9-]/g, '')}))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="半角英数・ハイフンのみ（例: toshin）" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={save} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">保存</button>
              <button onClick={() => setShowForm(false)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">キャンセル</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AgendaSection({ settings, token, showToast, setData }) {
  const [agendaUrl, setAgendaUrl] = useState(settings?.agenda_url || '');

  const saveAgendaUrl = async (url) => {
    await apiCall('PUT', '/settings', { agenda_url: url }, token);
    setData(prev => ({ ...prev, settings: { ...prev.settings, agenda_url: url } }));
  };

  return (
    <div className="flex flex-col gap-6 max-w-lg">
      <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
        <span className="text-slate-400">{SECTION_ICONS.agenda}</span>
        アジェンダ
      </h2>
      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-4">
        <h3 className="font-semibold text-slate-700">アジェンダ PDF</h3>
        <p className="text-xs text-slate-400">アップロードするとポータルのアジェンダタブに表示されます。</p>
        <div className="flex gap-2 items-center flex-wrap">
          <input
            className={`${inputCls} flex-1 min-w-0`}
            type="text"
            value={agendaUrl}
            onChange={e => setAgendaUrl(e.target.value)}
            placeholder="https:// または PDF をアップロード"
          />
          <FileUploadButton
            token={token}
            accept=".pdf,application/pdf"
            label="PDFをアップロード"
            onUploaded={async (url) => {
              setAgendaUrl(url);
              try { await saveAgendaUrl(url); showToast('アジェンダを更新しました'); }
              catch (e) { showToast(e.message, 'error'); }
            }}
            onError={msg => showToast(msg, 'error')}
          />
        </div>
        {agendaUrl && (
          <div className="flex items-center gap-2">
            <button type="button"
              onClick={async () => {
                try { await saveAgendaUrl(agendaUrl); showToast('アジェンダURLを保存しました'); }
                catch (e) { showToast(e.message, 'error'); }
              }}
              className="self-start px-4 py-1.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors">
              保存
            </button>
            <button type="button"
              onClick={async () => {
                const urlToDelete = agendaUrl;
                setAgendaUrl('');
                try {
                  await saveAgendaUrl('');
                  if (urlToDelete.startsWith('/api/files/')) {
                    await apiCall('DELETE', urlToDelete.replace('/api', ''), null, token);
                  }
                  showToast('アジェンダを削除しました');
                } catch (e) { showToast(e.message, 'error'); }
              }}
              className="text-xs text-red-500 hover:underline">
              削除
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Survey Section ────────────────────────────────────────────────────────────
const SCORE_CATEGORIES = [
  { label: '満足度', barColor: 'bg-amber-400', questions: ['研修全体の満足度', '講師の説明のわかりやすさ', '時間配分の適切さ', '業務意識の変化'] },
  { label: '理解度', barColor: 'bg-blue-500', questions: ['Gemini in Chat / Meet / カレンダー', 'Gemini in スプレッドシート', 'Gemini in ドキュメント', 'Gemini in Gmail', 'NotebookLM'] },
  { label: '活用イメージ', barColor: 'bg-emerald-500', questions: ['Chat / Meet / カレンダーで活用イメージが持てた', 'スプレッドシートで活用イメージが持てた', 'ドキュメントで活用イメージが持てた', 'Gmailで活用イメージが持てた', 'NotebookLMで活用イメージが持てた'] },
  { label: '次のステップ', barColor: 'bg-violet-500', questions: ['WDAアセスメントへの関心度'] },
];
const FREE_QUESTION_KEYWORDS = ['明日から活用したい業務', '不足していた内容・深掘りしたいテーマ', '研修全体への感想'];

function SurveySection({ settings, token, showToast, setData }) {
  const [surveyData, setSurveyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [webhookSecret, setWebhookSecret] = useState(settings?.survey_webhook_secret || '');
  const [savingSecret, setSavingSecret] = useState(false);
  const [copied, setCopied] = useState('');
  const [setupOpen, setSetupOpen] = useState(!settings?.survey_webhook_secret);

  const webhookUrl = typeof window !== 'undefined' ? `${window.location.origin}/api/survey-responses` : '/api/survey-responses';

  const appsScript = `function sendToWebhook(e) {
  var WEBHOOK_URL = '${webhookUrl}';
  var WEBHOOK_SECRET = '${webhookSecret || 'シークレットを生成してから再表示してください'}';
  var responses = e.response.getItemResponses();
  var answers = {};
  var satisfaction = null;
  responses.forEach(function(r) {
    var question = r.getItem().getTitle();
    var answer = r.getResponse();
    answers[question] = answer;
    if (question.includes('研修全体の満足度')) {
      satisfaction = parseInt(answer, 10) || null;
    }
  });
  UrlFetchApp.fetch(WEBHOOK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Webhook-Secret': WEBHOOK_SECRET },
    payload: JSON.stringify({ satisfaction: satisfaction, answers: answers }),
  });
}`;

  useEffect(() => {
    apiCall('GET', '/survey-responses', null, token)
      .then(d => { setSurveyData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const generateSecret = async () => {
    const newSecret = crypto.randomUUID().replace(/-/g, '');
    setSavingSecret(true);
    try {
      await apiCall('PUT', '/settings', { survey_webhook_secret: newSecret }, token);
      setWebhookSecret(newSecret);
      setData(prev => ({ ...prev, settings: { ...prev.settings, survey_webhook_secret: newSecret } }));
      showToast('シークレットを生成しました');
    } catch (e) { showToast(e.message, 'error'); }
    finally { setSavingSecret(false); }
  };

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text).then(() => { setCopied(key); setTimeout(() => setCopied(''), 2000); });
  };

  const exportCsv = () => {
    if (!surveyData?.responses?.length) return;
    const allKeys = new Set();
    surveyData.responses.forEach(r => Object.keys(r.answers || {}).forEach(k => allKeys.add(k)));
    const keys = [...allKeys];
    const header = ['回答日時', '満足度', ...keys].join(',');
    const rows = surveyData.responses.map(r => [
      `"${r.submitted_at}"`, r.satisfaction ?? '',
      ...keys.map(k => `"${String(r.answers?.[k] ?? '').replace(/"/g, '""')}"`),
    ].join(','));
    // Excel での文字化け対策として BOM を付与
    const blob = new Blob(['\uFEFF' + [header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `survey_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const clearAll = async () => {
    if (!confirm('全ての回答データを削除しますか？この操作は取り消せません。')) return;
    try {
      await apiCall('DELETE', '/survey-responses', null, token);
      setSurveyData({ stats: { total: 0, avg_satisfaction: null, distribution: [0,0,0,0,0] }, responses: [] });
      showToast('回答データを削除しました');
    } catch (e) { showToast(e.message, 'error'); }
  };

  const stats = surveyData?.stats;
  const responses = surveyData?.responses || [];
  const maxDist = stats ? Math.max(...stats.distribution, 1) : 1;

  const questionAvgs = useMemo(() => {
    const avgs = {};
    responses.forEach(r => {
      Object.entries(r.answers || {}).forEach(([q, a]) => {
        const score = parseInt(a, 10);
        if (!isNaN(score) && score >= 1 && score <= 5) {
          if (!avgs[q]) avgs[q] = { sum: 0, count: 0 };
          avgs[q].sum += score; avgs[q].count++;
        }
      });
    });
    return avgs;
  }, [responses]);

  const questionKeyMap = useMemo(() => {
    const map = {};
    Object.keys(questionAvgs).forEach(key => {
      SCORE_CATEGORIES.forEach(({ questions }) => {
        questions.forEach(qTitle => { if (key.includes(qTitle)) map[qTitle] = key; });
      });
    });
    return map;
  }, [questionAvgs]);

  const freeTexts = useMemo(() => {
    const texts = {};
    responses.forEach(r => {
      Object.entries(r.answers || {}).forEach(([q, a]) => {
        const kw = FREE_QUESTION_KEYWORDS.find(k => q.includes(k));
        if (kw && String(a).trim()) {
          if (!texts[q]) texts[q] = [];
          texts[q].push(String(a).trim());
        }
      });
    });
    return texts;
  }, [responses]);

  const StarIcon = ({ filled }) => (
    <svg className={`w-3.5 h-3.5 ${filled ? 'text-amber-400' : 'text-slate-200'}`} fill="currentColor" viewBox="0 0 20 20">
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );

  return (
    <div className="flex flex-col gap-6">
      <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
        <span className="text-slate-400">{SECTION_ICONS.survey}</span>
        アンケート集計
      </h2>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <button onClick={() => setSetupOpen(v => !v)} className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors cursor-pointer">
          <div className="flex items-center gap-2">
            <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="font-semibold text-slate-700 text-sm">Webhookセットアップ</span>
            {webhookSecret && <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">設定済み</span>}
          </div>
          <svg className={`w-4 h-4 text-slate-400 transition-transform ${setupOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {setupOpen && (
          <div className="px-5 pb-5 flex flex-col gap-4 border-t border-slate-100">
            <div className="flex flex-col gap-1.5 pt-4">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Webhook URL</label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-700 truncate font-mono">{webhookUrl}</code>
                <button onClick={() => copyText(webhookUrl, 'url')} className="flex-shrink-0 px-3 py-2 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl transition-colors">
                  {copied === 'url' ? '✓ コピー済み' : 'コピー'}
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Webhookシークレット</label>
              <div className="flex items-center gap-2">
                {webhookSecret ? (
                  <>
                    <code className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-slate-700 truncate font-mono">{webhookSecret}</code>
                    <button onClick={() => copyText(webhookSecret, 'secret')} className="flex-shrink-0 px-3 py-2 text-xs bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-xl transition-colors">
                      {copied === 'secret' ? '✓ コピー済み' : 'コピー'}
                    </button>
                  </>
                ) : (
                  <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 flex-1">シークレットが未設定です。生成してください。</p>
                )}
                <button onClick={generateSecret} disabled={savingSecret} className="flex-shrink-0 px-3 py-2 text-xs bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-colors disabled:opacity-50 cursor-pointer">
                  {savingSecret ? '生成中...' : webhookSecret ? '再生成' : '生成'}
                </button>
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Apps Script コード</label>
                <button onClick={() => copyText(appsScript, 'script')} className="text-xs px-3 py-1 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg transition-colors cursor-pointer">
                  {copied === 'script' ? '✓ コピー済み' : 'すべてコピー'}
                </button>
              </div>
              <pre className="text-xs bg-slate-900 text-slate-300 rounded-xl p-4 overflow-x-auto leading-relaxed font-mono whitespace-pre">{appsScript}</pre>
              <div className="text-xs text-slate-400 bg-slate-50 rounded-xl p-3 flex flex-col gap-0.5">
                <p className="font-medium text-slate-600 mb-1">設定手順：</p>
                <ol className="list-decimal list-inside flex flex-col gap-0.5 ml-1">
                  <li>Googleフォームを開き、右上メニュー → 「スクリプトエディタ」</li>
                  <li>上のコードをそのまま貼り付けて保存（変更不要）</li>
                  <li>時計アイコン（トリガー）→「トリガーを追加」→ イベント: <strong>フォーム送信時</strong>、関数: <strong>sendToWebhook</strong></li>
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-5">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-slate-700">集計結果</h3>
          {responses.length > 0 && (
            <div className="flex items-center gap-2">
              <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl transition-colors cursor-pointer">
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                CSV
              </button>
              <button onClick={clearAll} className="text-xs text-red-500 hover:text-red-700 hover:underline px-2 py-1.5 transition-colors cursor-pointer">全削除</button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="py-8 flex justify-center">
            <div className="w-6 h-6 border-2 border-slate-200 border-t-indigo-600 rounded-full animate-spin" />
          </div>
        ) : !stats || stats.total === 0 ? (
          <div className="py-10 text-center text-slate-400 border-2 border-dashed border-slate-200 rounded-2xl">
            <p className="text-sm">まだ回答がありません</p>
            <p className="text-xs mt-1">Webhookを設定してフォームへの回答を待ちましょう</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 rounded-xl p-4 flex flex-col gap-1">
                <p className="text-xs text-slate-500">総回答数</p>
                <p className="text-3xl font-bold text-slate-800">{stats.total}<span className="text-sm font-normal text-slate-400 ml-1">件</span></p>
              </div>
              <div className="bg-slate-50 rounded-xl p-4 flex flex-col gap-1">
                <p className="text-xs text-slate-500">研修全体の満足度（平均）</p>
                {stats.avg_satisfaction != null ? (
                  <div className="flex items-baseline gap-1.5 flex-wrap">
                    <p className="text-3xl font-bold text-slate-800">{Number(stats.avg_satisfaction).toFixed(1)}</p>
                    <p className="text-sm text-slate-400">/ 5</p>
                    <div className="flex">{[1,2,3,4,5].map(s => <StarIcon key={s} filled={s <= Math.round(stats.avg_satisfaction)} />)}</div>
                  </div>
                ) : <p className="text-sm text-slate-400">—</p>}
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">研修全体の満足度 — 分布</p>
              <div className="flex flex-col gap-1.5">
                {[5,4,3,2,1].map(star => {
                  const count = stats.distribution[star - 1] || 0;
                  return (
                    <div key={star} className="flex items-center gap-2 text-xs">
                      <div className="flex items-center gap-0.5 w-12 flex-shrink-0 justify-end">
                        <span className="text-slate-500">{star}</span>
                        <svg className="w-3 h-3 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      </div>
                      <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden">
                        <div className="h-full bg-amber-400 rounded-full transition-all duration-500" style={{ width: `${Math.round((count / maxDist) * 100)}%` }} />
                      </div>
                      <span className="w-8 text-right text-slate-500 flex-shrink-0">{count}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">カテゴリ別スコア</p>
              {SCORE_CATEGORIES.map(({ label, questions, barColor }) => {
                const rows = questions.map(qTitle => {
                  const key = questionKeyMap[qTitle];
                  const avg = key ? questionAvgs[key].sum / questionAvgs[key].count : null;
                  return { qTitle, avg };
                }).filter(r => r.avg !== null);
                if (rows.length === 0) return null;
                const catAvg = rows.reduce((s, r) => s + r.avg, 0) / rows.length;
                return (
                  <div key={label} className="flex flex-col gap-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-semibold text-slate-700">{label}</span>
                      <span className="text-sm font-bold text-slate-500">{catAvg.toFixed(1)} / 5</span>
                    </div>
                    <div className="flex flex-col gap-2 pl-3 border-l-2 border-slate-100">
                      {rows.map(({ qTitle, avg }) => (
                        <div key={qTitle} className="flex flex-col gap-1">
                          <p className="text-xs text-slate-500">{qTitle}</p>
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-slate-100 rounded-full h-2 overflow-hidden">
                              <div className={`h-full rounded-full ${barColor} transition-all duration-500`} style={{ width: `${(avg / 5) * 100}%` }} />
                            </div>
                            <span className="text-xs font-bold text-slate-700 w-8 text-right">{avg.toFixed(1)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {Object.keys(freeTexts).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-4">
          <h3 className="font-semibold text-slate-700">自由回答</h3>
          {Object.entries(freeTexts).map(([q, answers]) => (
            <div key={q} className="flex flex-col gap-2">
              <p className="text-xs font-semibold text-slate-600 bg-slate-50 rounded-lg px-3 py-1.5">{q.replace(/^\[.+?\]\s*/, '')}</p>
              <ul className="flex flex-col gap-1.5 pl-1">
                {answers.map((a, i) => <li key={i} className="text-sm text-slate-700 bg-white border border-slate-100 rounded-xl px-4 py-2.5">{a}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}

      {responses.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-3">
          <h3 className="font-semibold text-slate-700">回答一覧<span className="text-xs font-normal text-slate-400 ml-2">（最新{responses.length}件）</span></h3>
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-xs text-left">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2.5 font-semibold text-slate-600 whitespace-nowrap">日時</th>
                  <th className="px-3 py-2.5 font-semibold text-slate-600 whitespace-nowrap">名前</th>
                  <th className="px-3 py-2.5 font-semibold text-slate-600 whitespace-nowrap">会社名</th>
                  <th className="px-3 py-2.5 font-semibold text-slate-600 whitespace-nowrap">部署・役職</th>
                  <th className="px-3 py-2.5 font-semibold text-slate-600 whitespace-nowrap">満足度</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {responses.map(r => {
                  const nameKey    = Object.keys(r.answers || {}).find(k => k.includes('お名前'));
                  const companyKey = Object.keys(r.answers || {}).find(k => k.includes('会社名'));
                  const deptKey    = Object.keys(r.answers || {}).find(k => k.includes('部署'));
                  return (
                    <tr key={r.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{r.submitted_at?.slice(0, 16).replace('T', ' ')}</td>
                      <td className="px-3 py-2.5 text-slate-700">{nameKey    ? r.answers[nameKey]    : '—'}</td>
                      <td className="px-3 py-2.5 text-slate-600">{companyKey ? r.answers[companyKey] : '—'}</td>
                      <td className="px-3 py-2.5 text-slate-500 max-w-[140px] truncate">{deptKey ? r.answers[deptKey] : '—'}</td>
                      <td className="px-3 py-2.5">
                        {r.satisfaction != null ? (
                          <div className="flex items-center gap-0.5">{[1,2,3,4,5].map(s => <StarIcon key={s} filled={s <= r.satisfaction} />)}</div>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Settings Section ─────────────────────────────────────────────────────────
function SettingsSection({ settings, token, showToast, setData, sections, data }) {
  const [title, setTitle] = useState(settings?.training_title || '');
  const [trainingEdition, setTrainingEdition] = useState(settings?.training_edition || 'google_workspace');
  const [formUrl, setFormUrl] = useState(settings?.form_url || '');
  const [formQrUrl, setFormQrUrl] = useState(settings?.form_qr_url || '');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [participantPw, setParticipantPw] = useState('');
  const [participantPw2, setParticipantPw2] = useState('');
  const [saving, setSaving] = useState(false);

  const saveTitle = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await apiCall('PUT', '/settings', { training_title: title }, token); setData(prev => ({ ...prev, settings: { ...prev.settings, training_title: title } })); showToast('タイトルを保存しました'); }
    catch (e) { showToast(e.message, 'error'); } finally { setSaving(false); }
  };

  const saveEdition = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await apiCall('PUT', '/settings', { training_edition: trainingEdition }, token);
      setData(prev => ({ ...prev, settings: { ...prev.settings, training_edition: trainingEdition } }));
      showToast('研修版を変更しました');
    }
    catch (e) { showToast(e.message, 'error'); } finally { setSaving(false); }
  };

  const saveFormUrl = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await apiCall('PUT', '/settings', { form_url: formUrl }, token); setData(prev => ({ ...prev, settings: { ...prev.settings, form_url: formUrl } })); showToast('フォームURLを保存しました'); }
    catch (e) { showToast(e.message, 'error'); } finally { setSaving(false); }
  };

  const saveFormQrUrl = async (url) => {
    try { await apiCall('PUT', '/settings', { form_qr_url: url }, token); setData(prev => ({ ...prev, settings: { ...prev.settings, form_qr_url: url } })); }
    catch (e) { showToast(e.message, 'error'); }
  };

  const savePw = async (e) => {
    e.preventDefault();
    if (pw !== pw2) { showToast('パスワードが一致しません', 'error'); return; }
    if (pw.length < 6) { showToast('6文字以上で設定してください', 'error'); return; }
    setSaving(true);
    try { await apiCall('PUT', '/settings', { password: pw }, token); showToast('管理者パスワードを変更しました'); setPw(''); setPw2(''); }
    catch (e) { showToast(e.message, 'error'); } finally { setSaving(false); }
  };

  const saveParticipantPw = async (e) => {
    e.preventDefault();
    if (participantPw !== participantPw2) { showToast('パスワードが一致しません', 'error'); return; }
    if (participantPw.length < 4) { showToast('4文字以上で設定してください', 'error'); return; }
    setSaving(true);
    try { await apiCall('PUT', '/settings', { participant_password: participantPw }, token); showToast('参加者パスワードを設定しました'); setParticipantPw(''); setParticipantPw2(''); }
    catch (e) { showToast(e.message, 'error'); } finally { setSaving(false); }
  };

  return (
    <div className="flex flex-col gap-6 max-w-lg">
      <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
        <span className="text-slate-400">{SECTION_ICONS.settings}</span>
        設定
      </h2>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-4">
        <h3 className="font-semibold text-slate-700">研修タイトル</h3>
        <form onSubmit={saveTitle} className="flex flex-col gap-3">
          <input className={inputCls} value={title} onChange={e => setTitle(e.target.value)} placeholder="研修タイトルを入力" />
          <button type="submit" disabled={saving} className="self-start px-5 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors cursor-pointer">保存</button>
        </form>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-4">
        <h3 className="font-semibold text-slate-700">参加者向け研修版</h3>
        <p className="text-xs text-slate-400">
          このアカウント（本社または地域）の参加者が見る研修の版を設定します。
        </p>
        <form onSubmit={saveEdition} className="flex flex-col gap-3">
          <select className={inputCls} value={trainingEdition} onChange={e => setTrainingEdition(e.target.value)}>
            <option value="google_workspace">Google Workspace & AI 業務再設計編</option>
            <option value="genspark">Genspark リサーチ再設計編</option>
            <option value="claude">Claude Cowork 共創編</option>
          </select>
          <button type="submit" disabled={saving} className="self-start px-5 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors cursor-pointer">保存</button>
        </form>
      </div>

      {/* セクション管理 */}
      <SectionManager sections={sections} token={token} showToast={showToast} setData={setData} />

      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-5">
        <h3 className="font-semibold text-slate-700">アンケートフォーム</h3>
        <div className="flex flex-col gap-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">QRコード画像</label>
          <p className="text-xs text-slate-400">参加者のアンケートタブに表示されます。PNG・JPEG 推奨。</p>
          <div className="flex items-start gap-4">
            {formQrUrl && (
              <img
                src={formQrUrl.startsWith('/api/files/') ? `${formQrUrl}?token=${token}` : formQrUrl}
                alt="アンケートQRコード"
                className="w-24 h-24 object-contain border border-slate-200 rounded-xl bg-white flex-shrink-0"
              />
            )}
            <div className="flex flex-col gap-2 flex-1">
              <FileUploadButton
                token={token}
                accept="image/png,image/jpeg,image/gif,image/webp"
                label="画像をアップロード"
                onUploaded={async (url) => {
                  if (formQrUrl?.startsWith('/api/files/')) {
                    apiCall('DELETE', formQrUrl.replace('/api', ''), null, token).catch(() => {});
                  }
                  setFormQrUrl(url);
                  try { await saveFormQrUrl(url); showToast('QRコードを更新しました'); }
                  catch (e) { showToast(e.message, 'error'); }
                }}
                onError={msg => showToast(msg, 'error')}
              />
              {formQrUrl && (
                <button type="button"
                  onClick={async () => {
                    const old = formQrUrl; setFormQrUrl('');
                    try {
                      await saveFormQrUrl('');
                      if (old.startsWith('/api/files/')) apiCall('DELETE', old.replace('/api', ''), null, token).catch(() => {});
                      showToast('QRコードを削除しました');
                    } catch (e) { showToast(e.message, 'error'); }
                  }}
                  className="text-xs text-red-500 hover:underline self-start">
                  削除
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">フォーム URL（任意）</label>
          <p className="text-xs text-slate-400">設定するとポータル上でフォームをインライン表示します。</p>
          <form onSubmit={saveFormUrl} className="flex flex-col gap-2">
            <input className={inputCls} type="text" value={formUrl} onChange={e => setFormUrl(e.target.value)} placeholder="https://forms.gle/..." />
            <div className="flex items-center gap-3">
              <button type="submit" disabled={saving} className="self-start px-4 py-1.5 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors cursor-pointer">保存</button>
              {formUrl && (
                <button type="button"
                  onClick={async () => {
                    setFormUrl('');
                    try { await apiCall('PUT', '/settings', { form_url: '' }, token); setData(prev => ({ ...prev, settings: { ...prev.settings, form_url: '' } })); showToast('フォームURLを削除しました'); }
                    catch (e) { showToast(e.message, 'error'); }
                  }}
                  className="text-xs text-red-500 hover:underline">
                  削除
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-4">
        <h3 className="font-semibold text-slate-700">参加者パスワード</h3>
        <p className="text-xs text-slate-400">ポータルにログインする際に使う共通パスワードです。</p>
        <form onSubmit={saveParticipantPw} className="flex flex-col gap-3">
          <input className={inputCls} type="password" value={participantPw} onChange={e => setParticipantPw(e.target.value)} placeholder="参加者パスワード（4文字以上）" />
          <input className={inputCls} type="password" value={participantPw2} onChange={e => setParticipantPw2(e.target.value)} placeholder="パスワード（確認）" />
          <button type="submit" disabled={saving} className="self-start px-5 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors cursor-pointer">設定</button>
        </form>
        {/* 参加者URL */}
        {(() => {
          const tenantId = (() => { try { return JSON.parse(atob(token.split('.')[1])).tenant_id; } catch { return null; } })();
          const tenantSlug = tenantId ? (data?.tenants?.find(t => t.id === tenantId)?.slug || null) : null;
          const portalUrl = tenantId
            ? (tenantSlug ? `${window.location.origin}/t/${tenantSlug}` : `${window.location.origin}/?tenant=${tenantId}`)
            : window.location.origin + '/';
          return (
            <div className="mt-2 p-3 bg-slate-50 rounded-xl border border-slate-200">
              <p className="text-xs text-slate-500 mb-1.5 font-medium">参加者用ポータルURL</p>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-xs text-slate-700 bg-white border border-slate-200 rounded px-2 py-1.5 overflow-x-auto">{portalUrl}</code>
                <button onClick={() => { navigator.clipboard.writeText(portalUrl); showToast('URLをコピーしました'); }}
                  className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 whitespace-nowrap">
                  コピー
                </button>
              </div>
            </div>
          );
        })()}
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 flex flex-col gap-4">
        <h3 className="font-semibold text-slate-700">管理者パスワード変更</h3>
        <form onSubmit={savePw} className="flex flex-col gap-3">
          <input className={inputCls} type="password" value={pw} onChange={e => setPw(e.target.value)} placeholder="新しいパスワード（6文字以上）" />
          <input className={inputCls} type="password" value={pw2} onChange={e => setPw2(e.target.value)} placeholder="パスワード（確認）" />
          <button type="submit" disabled={saving} className="self-start px-5 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors cursor-pointer">変更</button>
        </form>
      </div>

    </div>
  );
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // URLからtenant_idを取得
  const tenantId = new URLSearchParams(window.location.search).get('tenant') || null;
  const isRegion = !!tenantId;

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      const body = tenantId
        ? { password, role: 'admin', tenant_id: tenantId }
        : { password, role: 'admin' };
      const data = await apiCall('POST', '/auth', body);
      onLogin(data.token);
    }
    catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 60%, #0f172a 100%)' }}>
      <div className="absolute -top-1/3 -left-1/4 w-[500px] h-[500px] bg-indigo-700/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-1/4 -right-1/4 w-[400px] h-[400px] bg-violet-700/15 rounded-full blur-3xl pointer-events-none" />
      <div className="relative w-full max-w-sm">
        <div className="flex justify-center mb-8">
          <div className="w-12 h-12 bg-slate-700 rounded-xl flex items-center justify-center border border-slate-600">
            <svg className="w-6 h-6 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
        </div>
        <div className="bg-white/[0.07] backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
          <h1 className="text-xl font-bold text-white mb-1.5 text-center font-display">管理者ログイン</h1>
          <p className="text-sm text-slate-500 text-center mb-8">管理者パスワードを入力してください</p>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              className="w-full px-4 py-3 bg-white/[0.08] border border-white/15 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-400/50 transition-all"
              placeholder="パスワード" autoFocus />
            {error && <p className="text-sm text-red-300 bg-red-500/10 border border-red-400/20 px-3 py-2.5 rounded-xl">{error}</p>}
            <button type="submit" disabled={loading}
              className="py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium disabled:opacity-50 transition-all text-sm border border-slate-600">
              {loading ? 'ログイン中...' : 'ログイン'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Admin Main ────────────────────────────────────────────────────────────────
export default function Admin() {
  const urlTenantId = new URLSearchParams(window.location.search).get('tenant') || null;
  const [token, setToken] = useState(() => {
    const t = localStorage.getItem('admin_token');
    if (!t) return null;
    try {
      const p = JSON.parse(atob(t.split('.')[1]));
      if (urlTenantId) {
        // ?tenant=付きURLに本社superトークンでアクセスした場合は自動ログアウト
        if (p.role === 'super') { localStorage.removeItem('admin_token'); return null; }
        // 別テナントのトークンが残っている場合もログアウト
        if (p.tenant_id && p.tenant_id !== urlTenantId) { localStorage.removeItem('admin_token'); return null; }
      } else {
        // テナントなしURLに地域管理者トークンでアクセスした場合は自動ログアウト
        if (p.role === 'region') { localStorage.removeItem('admin_token'); return null; }
      }
    } catch { return null; }
    return t;
  });

  // JWTからroleとtenant_idを取得
  const tokenPayload = useMemo(() => {
    if (!token) return {};
    try { return JSON.parse(atob(token.split('.')[1])); }
    catch { return {}; }
  }, [token]);
  const isSuper = tokenPayload.role === 'super';
  const [data, setData] = useState(null);
  const [section, setSection] = useState('agenda');
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  const handleLogin = useCallback((t) => {
    localStorage.setItem('admin_token', t);
    setToken(t);
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
  };

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    apiCall('GET', '/data', null, token)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { if (e.message === 'Unauthorized') handleLogout(); setLoading(false); });
  }, [token]);

  if (!token) return <LoginScreen onLogin={handleLogin} />;
  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-page">
        <div className="w-7 h-7 border-2 border-slate-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  const allTagItems = [
    ...(data.gems || []),
    ...(data.prompts || []),
    ...(data.notebooks || []),
  ];
  const sections = data.sections || [];
  const commonProps = { token, setData, showToast };

  return (
    <div className="min-h-screen bg-page flex">
      <aside className="w-56 flex-shrink-0 bg-slate-900 border-r border-slate-800 flex flex-col sticky top-0 h-screen">
        <div className="px-4 py-5 border-b border-slate-800">
          <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-widest mb-0.5">管理画面</p>
          <h1 className="font-bold text-white text-sm leading-tight font-display">研修リソースポータル</h1>
        </div>
        <nav className="flex-1 px-2 py-3 flex flex-col gap-0.5">
          {SECTIONS_NAV.filter(s => s.id !== 'tenants' || isSuper).map(s => (
            <button key={s.id} onClick={() => setSection(s.id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors text-left cursor-pointer ${section === s.id ? 'bg-indigo-600/20 text-indigo-300 border border-indigo-500/20' : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'}`}>
              <span className={section === s.id ? 'text-indigo-400' : 'text-slate-500'}>{SECTION_ICONS[s.id]}</span>
              <span>{s.label}</span>
            </button>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-slate-800 flex flex-col gap-1">
          <button
            onClick={() => { localStorage.setItem('portal_preview_token', token); window.open('/', '_blank'); }}
            className="w-full flex items-center gap-2 text-sm text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors py-2 px-3 rounded-xl cursor-pointer">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            参加者として表示
          </button>
          <button onClick={handleLogout}
            className="w-full flex items-center gap-2 text-sm text-slate-400 hover:text-red-400 hover:bg-red-500/10 transition-colors py-2 px-3 rounded-xl cursor-pointer">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            ログアウト
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <main className="flex-1 p-6 max-w-3xl w-full mx-auto">
          {section === 'gems'      && <ResourceSection resource="gems"      items={data.gems}      allTagItems={allTagItems} sections={sections} {...commonProps} />}
          {section === 'notebooks' && <ResourceSection resource="notebooks" items={data.notebooks} allTagItems={allTagItems} sections={sections} {...commonProps} />}
          {section === 'prompts'   && <ResourceSection resource="prompts"   items={data.prompts}   allTagItems={allTagItems} sections={sections} {...commonProps} />}
          {section === 'materials' && <ResourceSection resource="materials" items={data.materials} allTagItems={allTagItems} sections={sections} {...commonProps} />}
          {section === 'gemini-spreadsheet' && (
            <ResourceSection
              resource="materials"
              items={(data.materials || []).filter(m => m.name.includes('スプレッドシート'))}
              allTagItems={allTagItems}
              sections={sections}
              customLabel="Gemini in スプレッドシート"
              customIcon="📊"
              {...commonProps}
            />
          )}
          {section === 'gemini-docs' && (
            <ResourceSection
              resource="materials"
              items={(data.materials || []).filter(m => m.name.includes('ドキュメント'))}
              allTagItems={allTagItems}
              sections={sections}
              customLabel="Gemini in ドキュメント"
              customIcon="📝"
              {...commonProps}
            />
          )}
          {section === 'gemini-gmail' && (
            <ResourceSection
              resource="materials"
              items={(data.materials || []).filter(m => m.name.includes('Gmail'))}
              allTagItems={allTagItems}
              sections={sections}
              customLabel="Gemini in Gmail"
              customIcon="✉️"
              {...commonProps}
            />
          )}
          {section === 'agenda'    && <AgendaSection   settings={data.settings} {...commonProps} />}
          {section === 'survey'    && <SurveySection   settings={data.settings} {...commonProps} />}
          {section === 'settings'  && <SettingsSection settings={data.settings} sections={sections} data={data} {...commonProps} />}
          {section === 'tenants' && isSuper && <TenantSection tenants={data.tenants || []} {...commonProps} />}
        </main>
      </div>

      <Toast toast={toast} />
    </div>
  );
}
